import { type ServerLifecycleWelcomePayload } from "@t3tools/contracts";
import { scopedProjectKey, scopeProjectRef } from "@t3tools/client-runtime";
import {
  Outlet,
  createRootRouteWithContext,
  type ErrorComponentProps,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useEffectEvent, useRef } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { BasaDocumentDialog } from "../components/chat/BasaDocumentDialog";

import { APP_DISPLAY_NAME } from "../branding";
import { GuidedProjectDialog } from "../components/GuidedProjectDialog";
import { AppSidebarLayout } from "../components/AppSidebarLayout";
import { CommandPalette } from "../components/CommandPalette";
import { StaleClientToastCoordinator } from "../components/StaleClientToastCoordinator";
import {
  SlowRpcAckToastCoordinator,
  WebSocketConnectionCoordinator,
  WebSocketConnectionSurface,
} from "../components/WebSocketConnectionSurface";
import { Button } from "../components/ui/button";
import {
  AnchoredToastProvider,
  stackedThreadToast,
  ToastProvider,
  toastManager,
} from "../components/ui/toast";
import { deriveLogicalProjectKey, derivePhysicalProjectKeyFromPath } from "../logicalProject";
import {
  getServerConfigUpdatedNotification,
  ServerConfigUpdatedNotification,
  startServerStateSync,
  useServerConfig,
  useServerConfigUpdatedSubscription,
  useServerWelcomeSubscription,
} from "../rpc/serverState";
import { useStore } from "../store";
import { useUiStateStore } from "../uiStateStore";
import { syncBrowserChromeTheme } from "../hooks/useTheme";
import {
  ensureEnvironmentConnectionBootstrapped,
  getPrimaryEnvironmentConnection,
  startEnvironmentConnectionService,
} from "../environments/runtime";
import { configureClientTracing } from "../observability/clientTracing";
import {
  ensurePrimaryEnvironmentReady,
  getPrimaryKnownEnvironment,
  resolveInitialServerAuthGateState,
  updatePrimaryEnvironmentDescriptor,
} from "../environments/primary";
import { gateReachedServer, invalidateServerAuthGateState } from "../environments/primary/auth";
import {
  refreshPrimarySessionState,
  usePrimarySessionState,
  usePrimarySessionStatePolling,
} from "../environments/primary/sessionState";
import { getWsConnectionUiState, useWsConnectionStatus } from "../rpc/wsConnectionState";
import { PlatformAccessPage } from "../components/auth/PlatformAccessPage";
import { WorkspaceStatePage } from "../components/auth/WorkspaceStatePage";
import { STANDALONE_AUTH_ROUTES } from "../shared/auth/team";
import { onWebAddress } from "../shared/auth/web-address";
import { t } from "~/i18n";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  beforeLoad: async () => {
    // On the web address the gate asks the router first: a session it routes
    // to no workspace server has no descriptor to read, and the descriptor's
    // retries would hold a starting workspace's page for 15 s.
    if (onWebAddress()) {
      const authGateState = await resolveInitialServerAuthGateState();
      if (gateReachedServer(authGateState)) await ensurePrimaryEnvironmentReady();
      return { authGateState };
    }

    const [, authGateState] = await Promise.all([
      ensurePrimaryEnvironmentReady(),
      resolveInitialServerAuthGateState(),
    ]);
    return {
      authGateState,
    };
  },
  component: RootRouteView,
  errorComponent: RootRouteErrorView,
  head: () => ({
    meta: [{ name: "title", content: APP_DISPLAY_NAME }],
  }),
});

function RootRouteView() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const { authGateState } = Route.useRouteContext();
  const primaryEnvironmentAuthenticated = authGateState.status === "authenticated";

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      syncBrowserChromeTheme();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  if (authGateState.status === "requires-auth") {
    return <Outlet />;
  }

  if (authGateState.status === "platform-not-allowed") {
    return <PlatformAccessPage policy={authGateState.policy} />;
  }

  if (authGateState.status === "workspace-unavailable") {
    // Signing in as another account, an invite, and a password reset work
    // without a workspace.
    if (STANDALONE_AUTH_ROUTES.has(pathname)) return <Outlet />;
    return authGateState.refusal.state === "no_web_access" ? (
      <PlatformAccessPage policy="team-server" />
    ) : (
      <WorkspaceStatePage refusal={authGateState.refusal} />
    );
  }

  // `authGateState` is resolved once at router load, so a session that ends
  // mid-run leaves it reading "authenticated" while the app sits on the
  // sign-in route. Keying the shell off the route instead of that snapshot is
  // what stops the sign-in card rendering inside the sidebar, which is not a
  // state the product has: signing in is always a whole window.
  const onStandaloneAuthRoute = STANDALONE_AUTH_ROUTES.has(pathname);

  const appShell = (
    <CommandPalette>
      <AppSidebarLayout>
        <Outlet />
      </AppSidebarLayout>
      <GuidedProjectDialog />
      <BasaDocumentDialog />
    </CommandPalette>
  );

  // The watchers stay mounted either way. They are what notices the session
  // coming back, so unmounting them on the sign-in route would strand the
  // user on a surface that can no longer dismiss itself.
  const body = onStandaloneAuthRoute ? (
    <Outlet />
  ) : primaryEnvironmentAuthenticated ? (
    <WebSocketConnectionSurface>{appShell}</WebSocketConnectionSurface>
  ) : (
    appShell
  );

  return (
    <ToastProvider>
      <AnchoredToastProvider>
        {primaryEnvironmentAuthenticated ? <AuthenticatedTracingBootstrap /> : null}
        {primaryEnvironmentAuthenticated ? <SessionRevocationWatcher /> : null}
        {primaryEnvironmentAuthenticated ? <ServerStateBootstrap /> : null}
        <EnvironmentConnectionManagerBootstrap />
        {primaryEnvironmentAuthenticated ? <EventRouter /> : null}
        {primaryEnvironmentAuthenticated ? <WebSocketConnectionCoordinator /> : null}
        {primaryEnvironmentAuthenticated ? <SlowRpcAckToastCoordinator /> : null}
        {primaryEnvironmentAuthenticated ? <StaleClientToastCoordinator /> : null}
        {body}
      </AnchoredToastProvider>
    </ToastProvider>
  );
}

function RootRouteErrorView({ error, reset }: ErrorComponentProps) {
  const message = errorMessage(error);
  const details = errorDetails(error);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-red-500)_16%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {APP_DISPLAY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
          {t("routes/somethingWentWrong_trym24")}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => reset()}>
            {t("common/tryAgain_lshgks")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            {t("common/reloadApp_fa4qao")}
          </Button>
        </div>

        <details className="group mt-5 overflow-hidden rounded-lg border border-border/70 bg-background/55">
          <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-muted-foreground">
            <span className="group-open:hidden">{t("routes/showErrorDetails_3vr408")}</span>
            <span className="hidden group-open:inline">{t("routes/hideErrorDetails_wm8lsr")}</span>
          </summary>
          <pre className="max-h-56 overflow-auto border-t border-border/70 bg-background/80 px-3 py-2 text-xs text-foreground/85">
            {details}
          </pre>
        </details>
      </section>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return t("routes/anUnexpectedRouterError_l8udnk");
}

function errorDetails(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return t("routes/noAdditionalErrorDetails_j44ivz");
  }
}

function ServerStateBootstrap() {
  useEffect(() => {
    if (!getPrimaryKnownEnvironment()) {
      return;
    }

    return startServerStateSync(getPrimaryEnvironmentConnection().client.server);
  }, []);

  return null;
}

function AuthenticatedTracingBootstrap() {
  useEffect(() => {
    void configureClientTracing();
  }, []);

  return null;
}

/**
 * Owns both directions of the session/sign-in transition.
 *
 * Losing the server connection and losing the account look alike from here,
 * so the only safe reading of a momentary failure is "not yet known". Anything
 * that is not a confirmed revocation leaves the user where they are; when the
 * connection comes back the session is re-read at once rather than waiting out
 * the poll interval, and a sign-in surface that a blip put up is taken back
 * down.
 */
function SessionRevocationWatcher() {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { data } = usePrimarySessionState();
  const wsUiState = getWsConnectionUiState(useWsConnectionStatus());
  usePrimarySessionStatePolling(true);

  // The 60s poll decides how stale the UI may get, not how fast it recovers.
  // Re-reading on the reconnect edge is what makes the surface disappear when
  // the connection returns instead of lingering for most of a minute.
  const previousWsUiState = useRef(wsUiState);
  useEffect(() => {
    const reconnected = previousWsUiState.current !== "connected" && wsUiState === "connected";
    previousWsUiState.current = wsUiState;
    if (reconnected) {
      refreshPrimarySessionState();
    }
  }, [wsUiState]);

  useEffect(() => {
    if (!data) {
      return;
    }
    // "offline-grace" means the server cannot reach platform-api and the account
    // still stands — local work continues, so this must not navigate away.
    if (data.state === "offline-grace") {
      return;
    }
    // The account lost this server's platform, which the 12-minute refresh
    // brings in. A fresh bootstrap reads the same refusal and puts the access
    // page up in place of the app; sign-in would only mint the same token.
    if (data.rejection === "platform_not_allowed") {
      invalidateServerAuthGateState();
      window.location.reload();
      return;
    }
    if (data.state === "revoked" || !data.authenticated) {
      invalidateServerAuthGateState();
      navigate({ to: "/sign-in", replace: true });
      return;
    }
    // The session is good. If a dropped connection is what put the sign-in
    // surface up, retire it: the user never signed out, and asking them to
    // retype credentials they still hold is the bug being fixed here.
    // `/register` is deliberately excluded — arriving there is a choice.
    if (pathname === "/sign-in") {
      navigate({ to: "/", replace: true });
    }
  }, [data, navigate, pathname]);

  return null;
}

function EnvironmentConnectionManagerBootstrap() {
  const queryClient = useQueryClient();

  useEffect(() => {
    return startEnvironmentConnectionService(queryClient);
  }, [queryClient]);

  return null;
}

function EventRouter() {
  const setActiveEnvironmentId = useStore((store) => store.setActiveEnvironmentId);
  const navigate = useNavigate();
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const readPathname = useEffectEvent(() => pathname);
  const handledBootstrapThreadIdRef = useRef<string | null>(null);
  const seenServerConfigUpdateIdRef = useRef(getServerConfigUpdatedNotification()?.id ?? 0);
  const lastKeybindingsSuccessToastAtRef = useRef(0);
  const disposedRef = useRef(false);
  const serverConfig = useServerConfig();

  const handleWelcome = useEffectEvent((payload: ServerLifecycleWelcomePayload | null) => {
    if (!payload) return;

    updatePrimaryEnvironmentDescriptor(payload.environment);
    setActiveEnvironmentId(payload.environment.environmentId);
    void (async () => {
      await ensureEnvironmentConnectionBootstrapped(payload.environment.environmentId);
      if (disposedRef.current) {
        return;
      }

      if (!payload.bootstrapProjectId || !payload.bootstrapThreadId) {
        return;
      }
      const bootstrapEnvironmentState =
        useStore.getState().environmentStateById[payload.environment.environmentId];
      const bootstrapProject =
        bootstrapEnvironmentState?.projectById[payload.bootstrapProjectId] ?? null;
      const bootstrapProjectKey =
        (bootstrapProject ? deriveLogicalProjectKey(bootstrapProject) : null) ??
        (serverConfig?.cwd
          ? derivePhysicalProjectKeyFromPath(payload.environment.environmentId, serverConfig.cwd)
          : null) ??
        scopedProjectKey(
          scopeProjectRef(payload.environment.environmentId, payload.bootstrapProjectId),
        );
      useUiStateStore.getState().setProjectExpanded(bootstrapProjectKey, true);

      if (readPathname() !== "/") {
        return;
      }
      if (handledBootstrapThreadIdRef.current === payload.bootstrapThreadId) {
        return;
      }
      await navigate({
        to: "/$environmentId/$threadId",
        params: {
          environmentId: payload.environment.environmentId,
          threadId: payload.bootstrapThreadId,
        },
        replace: true,
      });
      handledBootstrapThreadIdRef.current = payload.bootstrapThreadId;
    })().catch(() => undefined);
  });

  const handleServerConfigUpdated = useEffectEvent(
    (notification: ServerConfigUpdatedNotification | null) => {
      if (!notification) return;

      const { id, payload, source } = notification;
      if (id <= seenServerConfigUpdateIdRef.current) {
        return;
      }
      seenServerConfigUpdateIdRef.current = id;
      if (source !== "keybindingsUpdated") {
        return;
      }

      const issue = payload.issues.find((entry) => entry.kind.startsWith("keybindings."));
      if (!issue) {
        const now = Date.now();
        if (now - lastKeybindingsSuccessToastAtRef.current < 2_000) {
          return;
        }
        lastKeybindingsSuccessToastAtRef.current = now;
        toastManager.add({
          type: "success",
          title: t("routes/keybindingsUpdated_huh3e0"),
          description: t("routes/keybindingsConfigurationReloadedSuccessfully_oavdga"),
        });
        return;
      }

      toastManager.add(
        stackedThreadToast({
          type: "warning",
          title: t("routes/invalidKeybindingsConfiguration_an6aqh"),
          description: issue.message,
        }),
      );
    },
  );

  useEffect(() => {
    if (!serverConfig) {
      return;
    }

    updatePrimaryEnvironmentDescriptor(serverConfig.environment);
    setActiveEnvironmentId(serverConfig.environment.environmentId);
  }, [serverConfig, setActiveEnvironmentId]);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  useServerWelcomeSubscription(handleWelcome);
  useServerConfigUpdatedSubscription(handleServerConfigUpdated);

  return null;
}
